import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User, Upload, Database, Settings as SettingsIcon, AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { User as UserEntity } from '@/api/entities';

import UserProfile from '../components/admin/UserProfile';
import DataUploader from '../components/admin/DataUploader';
import BulkDataManager from '../components/admin/BulkDataManager';
import MultiTenantAccessManager from '../components/admin/MultiTenantAccessManager';
import TenantOverridesManager from '../components/admin/TenantOverridesManager';
import BatchFieldUpdater from '../components/admin/BatchFieldUpdater';
import PersonaRationaleViewer from '../components/admin/PersonaRationaleViewer';
import AccountNameManager from '../components/admin/AccountNameManager';
import CountryManager from '../components/admin/CountryManager';
import HideHongKongAccounts from '../components/admin/HideHongKongAccounts';
import BVOUploader from '../components/admin/BVOUploader';
import DigitalFundingUploader from '../components/admin/DigitalFundingUploader';
import SocialPresenceUploader from '../components/admin/SocialPresenceUploader';
import IncumbentSentimentUploader from '../components/admin/IncumbentSentimentUploader';
import PersonRoleTitleUploader from '../components/admin/PersonRoleTitleUploader';
import DepartmentDataUploader from '../components/admin/DepartmentDataUploader';
import PersonaAccountReconciler from '../components/admin/PersonaAccountReconciler';
import QuickEditManager from '../components/admin/QuickEditManager';
import PersonaFitScoreUploader from '../components/admin/PersonaFitScoreUploader';
import PersonaPhotoUpdater from '../components/admin/PersonaPhotoUpdater';
import BatchVisibilityManager from '../components/admin/BatchVisibilityManager';
import DepartmentUploader from '../components/admin/DepartmentUploader';
import DepartmentManager from '../components/admin/DepartmentManager';
import PersonaPhotoManager from '../components/admin/PersonaPhotoManager';
import PersonaDeletionManager from '../components/admin/PersonaDeletionManager';
import PersonContactInfoUploader from '../components/admin/PersonContactInfoUploader';
import PersonaSearchManager from '../components/admin/PersonaSearchManager';
import PersonaContactUploader from '../components/admin/PersonaContactUploader';
import EventsAttendedUploader from '../components/admin/EventsAttendedUploader';
import EventsAttendedFilter from '../components/admin/EventsAttendedFilter';
import UniversalFieldEditor from '../components/admin/UniversalFieldEditor';
import EmailNotificationsManager from '../components/admin/EmailNotificationsManager';
import PilotModeControl from '../components/admin/PilotModeControl';
import AccountDataExporter from '../components/admin/AccountDataExporter';
import ChatbotAccountRequests from '../components/admin/ChatbotAccountRequests';

const SettingsGmail = lazy(() => import('../pages/SettingsGmail'));
const SettingsPersonalization = lazy(() => import('../pages/SettingsPersonalization'));
const SettingsCommsSchedule = lazy(() => import('../pages/SettingsCommsSchedule'));
const SettingsEvents = lazy(() => import('../pages/SettingsEvents'));

class SettingsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Settings component error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load this setting. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      );
    }

    return this.props.children;
  }
}

export default function Admin({ currentTenant }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadUser = async () => {
    setIsLoading(true);
    try {
      const userData = await UserEntity.me();
      setUser(userData);
      console.log('👤 Admin loaded user:', { 
        email: userData.email, 
        role: userData.role, 
        tenant_id: userData.tenant_id,
        currentTenant: currentTenant
      });
    } catch (error) {
      console.error('Error loading user:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-600">Please log in to access admin settings.</p>
      </div>
    );
  }

  if (user.role !== 'admin') {
    return (
      <div className="text-center py-20">
        <p className="text-gray-600">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <Tabs defaultValue="quick-edit" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="quick-edit">Quick Edit</TabsTrigger>
          <TabsTrigger value="chat-requests">Chat requests</TabsTrigger>
          <TabsTrigger value="data">Data Management</TabsTrigger>
        </TabsList>

        <TabsContent value="quick-edit" className="space-y-6 mt-6">
          <UniversalFieldEditor />
          <PersonaSearchManager currentUser={user} />
          <QuickEditManager />
          <PersonaDeletionManager />
        </TabsContent>

        <TabsContent value="chat-requests" className="space-y-6 mt-6">
          <ChatbotAccountRequests currentUser={user} currentTenant={currentTenant} />
        </TabsContent>

        <TabsContent value="data" className="space-y-6 mt-6">
          <PilotModeControl currentTenant={currentTenant} />
          <AccountDataExporter />
          <EmailNotificationsManager />
          <EventsAttendedFilter />
          <EventsAttendedUploader />
          <PersonaContactUploader />
          <PersonContactInfoUploader />
          <DepartmentUploader />
          <BatchVisibilityManager />
          <DataUploader />
          <PersonaFitScoreUploader />
          <SocialPresenceUploader />
          <DepartmentDataUploader />
          <BulkDataManager />
          <PersonaAccountReconciler />
          <MultiTenantAccessManager />
          <TenantOverridesManager />
          <BatchFieldUpdater />
          <AccountNameManager />
          <HideHongKongAccounts />
          <BVOUploader />
          <DigitalFundingUploader />
          <IncumbentSentimentUploader />
          <PersonRoleTitleUploader />
        </TabsContent>
      </Tabs>
    </div>
  );
}